import type { Box } from './onscreen.ts';
import type { LatLngTuple } from './types.ts';

type Path = readonly LatLngTuple[];
interface Chunk {
  readonly start: number;
  readonly end: number;
  readonly box: Box;
}

// Index each path once. A long regional route should contribute only its
// nearby stretches to MapLibre; the full path stays available to trains.
const chunks = new WeakMap<Path, readonly Chunk[]>();
const POINTS = 64;

function index(path: Path): readonly Chunk[] {
  const cached = chunks.get(path);
  if (cached) return cached;
  const out: Chunk[] = [];
  for (let start = 0; start < path.length - 1; start += POINTS) {
    const end = Math.min(start + POINTS, path.length - 1);
    let west = Infinity;
    let south = Infinity;
    let east = -Infinity;
    let north = -Infinity;
    for (let i = start; i <= end; i++) {
      const p = path[i];
      if (!p) continue;
      south = Math.min(south, p[0]);
      north = Math.max(north, p[0]);
      west = Math.min(west, p[1]);
      east = Math.max(east, p[1]);
    }
    out.push({ start, end, box: [west, south, east, north] });
  }
  chunks.set(path, out);
  return out;
}

function intersects(a: Box, b: Box): boolean {
  if (a[1] > b[3] || a[3] < b[1]) return false;
  const shift = 360 * Math.round((a[0] + a[2] - b[0] - b[2]) / 720);
  return a[0] <= b[2] + shift && a[2] >= b[0] + shift;
}

// Include crossing segments even when neither endpoint is in view. Adjacent
// chunks are joined, preserving rail offsets and avoiding seams in the view.
export function visiblePaths(path: Path, view: Box): readonly Path[] {
  const out: Path[] = [];
  let start = -1;
  let end = 0;
  const flush = (): void => {
    if (start >= 0) out.push(start === 0 && end === path.length - 1 ? path : path.slice(start, end + 1));
    start = -1;
  };
  for (const chunk of index(path)) {
    if (intersects(view, chunk.box)) {
      if (start < 0) start = chunk.start;
      end = chunk.end;
    } else flush();
  }
  flush();
  return out;
}
